function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight + 1}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.header-wrapper');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight + 1}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);


if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

if (!customElements.get('modal-opener')) {
  customElements.define(
    'modal-opener',
    class ModalOpener extends HTMLElement {
      constructor() {
        super();

        const button = this.querySelector('button');
        if (!button) return;

        button.addEventListener('click', () => {
          const modal = document.querySelector(this.getAttribute('data-modal'));

          if (modal && typeof modal.show === 'function') {
            modal.show(button);
          } else {
            console.error('Modal does not exist or has no show() method:', modal);
          }
        });
      }
    }
  );
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);


class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.groupSize = parseInt(this.dataset.groupSize || 1);

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(() => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    this.dots = Array.from(this.querySelectorAll('.slider-dots .dot'));
    // if this.dots exists...
    if (this.dots?.length) {
      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.groupIndex - 1);
          const scrollTarget = index * this.groupSize * this.sliderItemOffset;
          this.setSlidePosition(scrollTarget);
        });
      });
    }

  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(el => el.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;

    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.totalGroups = Math.ceil(this.sliderItemsToShow.length / this.groupSize);
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    const scrollLeft = this.slider.scrollLeft;
    const groupOffset = this.sliderItemOffset * this.groupSize;

    this.currentPage = Math.round(scrollLeft / groupOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalGroups;
    }

    if (this.currentPage !== previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.sliderItemsToShow[(this.currentPage - 1) * this.groupSize],
        },
      }));
    }

    if (this.enableSliderLooping) return;

    const atStart = this.slider.scrollLeft === 0;
    const atEnd = this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1]);

    this.prevButton.toggleAttribute('disabled', atStart);
    this.nextButton.toggleAttribute('disabled', atEnd);

    if (this.dots?.length) {
      this.dots.forEach((dot, i) => {
        if (i === this.currentPage - 1) {
          dot.classList.add('dot--active');
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.classList.remove('dot--active');
          dot.removeAttribute('aria-current');
        }
      });
    }

  }

  isSlideVisible(element, offset = 0) {
    const lastVisible = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisible &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const step = parseInt(event.currentTarget.dataset.step || 1);
    const direction = event.currentTarget.name === 'next' ? 1 : -1;
    const groupOffset = this.sliderItemOffset * this.groupSize;

    this.slideScrollPosition = this.slider.scrollLeft + direction * step * groupOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    const groupWidth = this.sliderItemOffset * this.groupSize;
    const centerOffset = (this.slider.clientWidth - groupWidth) / 2;

    this.slider.scrollTo({
      left: position - centerOffset,
      behavior: 'smooth',
    });
  }
}

customElements.define('slider-component', SliderComponent);


class SlideshowComponent extends HTMLElement {
  constructor() {
    super();
    this.lastDotIndex = null;
    this.lastRenderedDotIndex = -1;
    this.requestedPage = 0;
    this.slider = this.querySelector('.slider');
    this.slides = Array.from(this.querySelectorAll('.slideshow__slide'));
    if (!this.dots?.length) {
      this.dots = Array.from(this.querySelectorAll('.slider-counter__link'));
    }
    this.updateDotsOnce = this.updateDotsOnce.bind(this); // ensure bound
    requestAnimationFrame(() => {
      this.goToPage(0); // this sets scroll
      this.updateDotsOnce(); // this ensures dot is marked
    });
    this.prevButton = this.querySelector('.slider-button--prev');
    this.nextButton = this.querySelector('.slider-button--next');

    this.slidesPerView = this.getSlidesPerView();
    if (this.slider.classList.contains('product__media-list')) this.slidesPerView = 1;

    this.currentPage = 0;
    this.autoRotate = this.slider?.dataset.autoplay === 'true';
    this.autoRotateSpeed = Number(this.slider?.dataset.speed || 5) * 1000;

    this.attachEvents();
    this.updatePagination();
    this.observeResize();
    this.initSlideObserver();

    if (this.autoRotate) this.startAutoplay();
  }

  getSlidesPerView() {
    return window.innerWidth >= 1200 ? 3 : window.innerWidth >= 1024 ? 2 : 1;
  }

  attachEvents() {
    this.prevButton?.addEventListener('click', () => this.goToPage(this.currentPage - 1));
    this.nextButton?.addEventListener('click', () => this.goToPage(this.currentPage + 1));
    this.dots.forEach((dot, i) =>
      dot.addEventListener('click', () => {
        this.goToPage(i);
        this.scrollThumbnailIntoView(i);
      })
    );
    this.slider.addEventListener('scroll', () => this.updateCurrentPage());
  }

  observeResize() {
    if (this.slider.classList.contains('product__media-list')) return;
    const resizeObserver = new ResizeObserver(() => {
      const newPerView = this.getSlidesPerView();
      if (newPerView !== this.slidesPerView) {
        this.slidesPerView = newPerView;
        this.updatePagination();
        this.goToPage(0);
        this.updateDotsOnce();
      }
    });
    resizeObserver.observe(this);
  }

  updateCurrentPage() {
    const slideWidth = this.slides[0].offsetWidth;
    const scrollLeft = this.slider.scrollLeft;
    this.currentPage = Math.round(scrollLeft / (slideWidth * this.slidesPerView));
    this.updateDotsOnce();
  }

  updatePagination() {
    const pageCount = Math.ceil(this.slides.length / this.slidesPerView);
    this.dots.forEach((dot, i) => {
      dot.style.display = i < pageCount ? 'inline-block' : 'none';
    });
  }

  updateDotsOnce() {
    if (this.currentPage === this.lastRenderedDotIndex) return;
    this.lastRenderedDotIndex = this.currentPage;

    setTimeout(() => {
      this.dots.forEach((dot, i) => {
        if (i === this.currentPage) {
          dot.classList.add('slider-counter__link--active');
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.classList.remove('slider-counter__link--active');
          dot.removeAttribute('aria-current');
        }
      });

      // Update thumbnail active states for product galleries
      this.updateThumbnailStates();
    }, 100)
  }

  goToPage(page) {
    const maxPage = Math.ceil(this.slides.length / this.slidesPerView) - 1;

    if (page > maxPage) {
      page = 0; // loop to start
    } else if (page < 0) {
      page = maxPage; // loop to end (optional)
    }

    this.requestedPage = page;
    this.currentPage = page;

    const slideWidth = this.slides[0].offsetWidth;
    const scrollTarget = slideWidth * this.slidesPerView * this.currentPage;

    this.slider.scrollTo({ left: scrollTarget, behavior: 'smooth' });

    // Let scroll/observer update the dots — don't force it here
  }

  startAutoplay() {
    this.autoplayInterval = setInterval(() => {
      const maxPage = Math.ceil(this.slides.length / this.slidesPerView) - 1;
      this.goToPage(this.currentPage >= maxPage ? 0 : this.currentPage + 1);
    }, this.autoRotateSpeed);
  }

  initSlideObserver() {
    const options = {
      root: this.slider,
      threshold: 0.6,
    };

    this.observer = new IntersectionObserver((entries) => {
      const visibleSlides = entries.filter(entry => entry.isIntersecting);

      if (!visibleSlides.length) return;

      const firstVisibleIndex = this.slides.indexOf(visibleSlides[0].target);
      const groupIndex = Math.floor(firstVisibleIndex / this.slidesPerView);

      if (groupIndex !== this.currentPage) {
        this.currentPage = groupIndex;
        this.updateDotsOnce();
      }
    }, options);

    this.slides.forEach(slide => this.observer.observe(slide));
  }

  updateThumbnailStates() {
    // Only handle product media galleries
    if (!this.slider.classList.contains('product__media-list')) return;

    // Find the section ID from the gallery
    const sectionId = this.slider.closest('[id^="Gallery-Viewer-"]')?.id.replace('Gallery-Viewer-', '');
    if (!sectionId) return;

    // Find the corresponding thumbnail slider
    const thumbnailSlider = document.querySelector(`#GalleryThumbnails-${sectionId}`);
    if (!thumbnailSlider) return;

    // Get all thumbnail items
    const thumbnailItems = Array.from(thumbnailSlider.querySelectorAll('.thumbnail-list__item'));

    // Update active states
    thumbnailItems.forEach((item, index) => {
      if (index === this.currentPage) {
        item.classList.add('is-active');
      } else {
        item.classList.remove('is-active');
      }
    });
  }

  scrollThumbnailIntoView(pageIndex) {
    // Only handle product media galleries
    if (!this.slider.classList.contains('product__media-list')) return;

    // Find the section ID from the gallery
    const sectionId = this.slider.closest('[id^="Gallery-Viewer-"]')?.id.replace('Gallery-Viewer-', '');
    if (!sectionId) return;

    // Find the corresponding thumbnail slider
    const thumbnailSlider = document.querySelector(`#GalleryThumbnails-${sectionId}`);
    const thumbnailList = thumbnailSlider?.querySelector('.thumbnail-list');

    if (!thumbnailList) return;

    // Calculate the scroll position for the thumbnail
    const thumbnailHeight = 65; // Height of each thumbnail
    const gap = 16; // Gap between thumbnails
    const scrollPosition = pageIndex * (thumbnailHeight + gap);

    // Smooth scroll the thumbnail into view
    thumbnailList.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);


class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

class RichTextFade extends HTMLElement {
  constructor() {
    super()
    this.handleScroll = this.handleScroll.bind(this)
  }

  connectedCallback() {
    this.words = this.querySelectorAll('.rich-text__text--word')
    this.totalWords = this.words.length

    this.overlapWords = parseInt(this.getAttribute('overlap-words') || 3)
    this.fadeRange = parseFloat(this.getAttribute('fade-range') || 0.4)
    this.thresholdPx = parseInt(this.getAttribute('threshold') || 200)

    window.addEventListener('scroll', this.handleScroll)
    this.handleScroll()
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll)
  }

  handleScroll() {
    const rect = this.getBoundingClientRect()
    const viewportHeight = window.innerHeight

    const visibleHeight = viewportHeight - rect.top

    if (visibleHeight < this.thresholdPx) {
      this.words.forEach((word) => (word.style.opacity = 0))
      return
    }

    let progress = (visibleHeight - this.thresholdPx) / (viewportHeight + rect.height - this.thresholdPx)
    progress = Math.max(0, Math.min(1, progress))

    const adjustedProgress = progress / this.fadeRange

    this.words.forEach((word, index) => {
      const sliceSize = 1 / (this.totalWords - this.overlapWords + 1)
      const wordStart = sliceSize * index
      const wordEnd = wordStart + sliceSize * this.overlapWords

      let opacity = (adjustedProgress - wordStart) / (wordEnd - wordStart)
      opacity = Math.max(0, Math.min(1, opacity))

      word.style.opacity = opacity
    })
  }
}

if (!customElements.get('rich-text-fade')) {
  customElements.define('rich-text-fade', RichTextFade)
}


class FeaturedCollection extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.imageBlocks = this.querySelectorAll('[data-image-index]');
    this.ctaButton = this.querySelector('.special-pool-top-btn');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.currentSlideIndex = 0;
    this.totalSlides = this.sliderItems.length;

    this.init();
    this.setupEventListeners();
  }

  init() {
    // Set initial active state
    this.updateActiveSlide(0);
    this.updateButtonStates();
  }

  setupEventListeners() {
    this.prevButton.addEventListener('click', () => this.goToPrevious());
    this.nextButton.addEventListener('click', () => this.goToNext());

    // Add touch events for mobile
    if ('ontouchstart' in window) {
      this.slider.addEventListener('touchstart', this.onTouchStart.bind(this));
      this.slider.addEventListener('touchend', this.onTouchEnd.bind(this));
    }
  }

  goToPrevious() {
    if (this.currentSlideIndex > 0) {
      this.currentSlideIndex--;
      this.goToSlide(this.currentSlideIndex);
    }
  }

  goToNext() {
    if (this.currentSlideIndex < this.totalSlides - 1) {
      this.currentSlideIndex++;
      this.goToSlide(this.currentSlideIndex);
    }
  }

  goToSlide(index) {
    if (index < 0 || index >= this.totalSlides) return;

    this.currentSlideIndex = index;
    this.updateActiveSlide(index);
    this.scrollToSlide(index);
    this.updateButtonStates();
  }

  scrollToSlide(index) {
    const targetSlide = this.sliderItems[index];
    if (!targetSlide) return;

    const slideLeft = targetSlide.offsetLeft;
    const slideWidth = targetSlide.offsetWidth;
    const containerWidth = this.slider.clientWidth;

    // Center the slide in the container
    const scrollLeft = slideLeft - (containerWidth - slideWidth) / 2;

    this.slider.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: 'smooth'
    });
  }

  updateActiveSlide(index) {
    // Remove active class from all slides
    this.sliderItems.forEach(slide => slide.classList.remove('is-active'));

    // Add active class to current slide
    if (this.sliderItems[index]) {
      this.sliderItems[index].classList.add('is-active');
    }

    // Update image visibility
    const slideIndex = index + 1; // data-image-index is 1-based
    this.imageBlocks.forEach((block) => {
      const isActive = block.dataset.imageIndex == slideIndex;
      block.classList.toggle('tw-opacity-100', isActive);
      block.classList.toggle('tw-opacity-0', !isActive);
      block.classList.toggle('tw-pointer-events-none', !isActive);
    });

    // Update CTA button
    const activeSlide = this.sliderItems[index];
    if (this.ctaButton && activeSlide) {
      const productUrl = activeSlide.dataset.productUrl;
      if (this.ctaButton && productUrl) {
        this.ctaButton.setAttribute('href', productUrl);
        this.ctaButton.setAttribute('aria-label', `See more about ${productUrl}`);
      }
    }
  }

  updateButtonStates() {
    this.prevButton.disabled = this.currentSlideIndex === 0;
    this.nextButton.disabled = this.currentSlideIndex === this.totalSlides - 1;
  }

  update() {
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    const scrollLeft = this.slider.scrollLeft;
    const groupOffset = this.sliderItemOffset * this.groupSize;

    this.currentPage = Math.round(scrollLeft / groupOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalGroups;
    }

    if (this.currentPage !== previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.sliderItemsToShow[(this.currentPage - 1) * this.groupSize],
        },
      }));
    }

    if (this.enableSliderLooping) return;

  const atStart = scrollLeft <= this.sliderItemOffset / 4;

  // Check if we're at the end by seeing if the last slide is fully visible
  const lastSlide = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
  const lastSlideRight = lastSlide.offsetLeft + lastSlide.offsetWidth;
  const containerRight = this.slider.scrollLeft + this.slider.clientWidth;
  const atEnd = lastSlideRight <= containerRight + 10; // 10px tolerance

  this.prevButton.toggleAttribute('disabled', atStart);
  this.nextButton.toggleAttribute('disabled', atEnd);
  }

  isSlideVisible(element, offset = 0) {
    const lastVisible = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisible &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

    onButtonClick(event) {
    event.preventDefault();

    const step = parseInt(event.currentTarget.dataset.step || 1);
    const direction = event.currentTarget.name === 'next' ? 1 : -1;

    // Find the currently active slide
    const currentActiveSlide = this.sliderItemsToShow.find(slide =>
      slide.classList.contains('is-active')
    );

    if (!currentActiveSlide) return;

    const currentIndex = this.sliderItemsToShow.indexOf(currentActiveSlide);
    let targetIndex = currentIndex + (direction * step);

    // Ensure we stay within bounds
    targetIndex = Math.max(0, Math.min(targetIndex, this.sliderItemsToShow.length - 1));

    const targetSlide = this.sliderItemsToShow[targetIndex];

    if (targetSlide) {
      this.centerSlide(targetSlide);
    }
  }

  onTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
    this.touchStartScrollLeft = this.slider.scrollLeft;
  }

  onTouchEnd(event) {
    if (!this.touchStartX) return;

    const touchEndX = event.changedTouches[0].clientX;
    const touchDiff = this.touchStartX - touchEndX;
    const threshold = 50;

    if (Math.abs(touchDiff) > threshold) {
      if (touchDiff > 0) {
        // Swiped left - go to next
        this.goToNext();
      } else {
        // Swiped right - go to previous
        this.goToPrevious();
      }
    }

    this.touchStartX = null;
  }

  setSlidePosition(position) {
    const groupWidth = this.sliderItemOffset * this.groupSize;
    const centerOffset = (this.slider.clientWidth - groupWidth) / 2;

    this.slider.scrollTo({
      left: position - centerOffset,
      behavior: 'smooth',
    });
  }

  centerSlide(slideElement) {
    const slideCenter = slideElement.offsetLeft + slideElement.offsetWidth / 2;
    const containerCenter = this.slider.clientWidth / 2;

    // Calculate target scroll position
    let targetScrollLeft = slideCenter - containerCenter;

    // Ensure we don't scroll beyond the available content
    const maxScrollLeft = this.slider.scrollWidth - this.slider.clientWidth;
    targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

    this.slider.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });

    this.updateActiveClass(slideElement);
  }

  updateActiveClass(activeElement) {
    this.sliderItemsToShow.forEach(el => el.classList.remove('is-active'));
    if (activeElement) {
      activeElement.classList.add('is-active');

      const slideIndex = activeElement.dataset.slideId;

      // Update image visibility
      this.imageBlocks.forEach((block) => {
        block.classList.toggle('tw-opacity-100', block.dataset.imageIndex === slideIndex);
        block.classList.toggle('tw-opacity-0', block.dataset.imageIndex !== slideIndex);
        block.classList.toggle('tw-pointer-events-none', block.dataset.imageIndex !== slideIndex);
      });

      // Update CTA href and label
      const productUrl = activeElement.dataset.productUrl;
      if (this.ctaButton && productUrl) {
        this.ctaButton.setAttribute('href', productUrl);
        this.ctaButton.setAttribute('aria-label', `See more about ${productUrl}`);
      }
    }
  }
}

customElements.define('featured-collection', FeaturedCollection);

class FeaturedSlider extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = Array.from(this.querySelectorAll('[id^="Slide-"]'));
    this.dots = Array.from(this.querySelectorAll('.slider-dots .dot'));
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.currentGroupIndex = 0;

    if (!this.slider || this.sliderItems.length === 0) return;

    this.handleResize = this.handleResize.bind(this);
    this.onScroll = this.onScroll.bind(this);

    this.buildGroups();
    this.showGroup(0);

    // Listeners
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
    this.prevButton?.addEventListener('click', () => this.showGroup(this.currentGroupIndex - 1));
    this.nextButton?.addEventListener('click', () => this.showGroup(this.currentGroupIndex + 1));

    this.onScroll();
  }

  getResponsiveGroupSize() {
    const width = window.innerWidth;
    if (width < 768) return 1; // mobile
    if (width < 1024) return 2; // tablet
    return 3; // desktop
  }

  buildGroups() {
    this.groupSize = this.getResponsiveGroupSize();
    this.totalGroups = Math.ceil(this.sliderItems.length / this.groupSize);
    this.groups = [];

    for (let i = 0; i < this.totalGroups; i++) {
      const start = i * this.groupSize;
      const end = start + this.groupSize;
      this.groups.push(this.sliderItems.slice(start, end));
    }

    // Rebuild dots if needed
    if (this.dots?.length !== this.totalGroups) {
      const dotsContainer = this.querySelector('.slider-dots');
      if (dotsContainer) {
        dotsContainer.innerHTML = '';
        for (let i = 0; i < this.totalGroups; i++) {
          const button = document.createElement('button');
          button.className = 'slider-counter__link slider-counter__link--dots link';
          button.dataset.groupIndex = i + 1;
          const dot = document.createElement('span');
          dot.className = 'dot';
          button.appendChild(dot);
          dot.addEventListener('click', () => this.showGroup(i));
          dotsContainer.appendChild(button);
        }
        this.dots = Array.from(dotsContainer.querySelectorAll('.dot'));
      }
    }
  }

  handleResize() {
    const prevGroupSize = this.groupSize;
    this.buildGroups();

    // Only reset view if group size changed
    if (this.groupSize !== prevGroupSize) {
      this.currentGroupIndex = 0;
      this.showGroup(0);
    }

    // Recalculate height
    const maxHeight = Math.max(...this.sliderItems.map(el => el.offsetHeight));
    this.slider.style.minHeight = `${maxHeight}px`;
  }

  onScroll() {
    const rect = this.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const sectionTop = rect.top;
    const sectionHeight = rect.height;
    const triggerStart = windowHeight * 0.7; // Animation starts earlier (lower value = earlier start)
    const triggerEnd = windowHeight * 0.3;   // Animation ends (slide fully in) before center

    const activeGroup = this.groups[this.currentGroupIndex];
    if (!activeGroup) return;

    activeGroup.forEach((item, i) => {
      // Calculate progress from 0 (start animating) to 1 (fully visible)
      let progress = (triggerStart - sectionTop) / (triggerStart - triggerEnd);
      progress = Math.max(0, Math.min(1, progress));
      // Optional: ease progress
      progress = 1 - Math.pow(1 - progress, 2.5);

      const translateY = 50 * (1 - progress); // 50px offset at start, 0px at end
      item.style.transform = `translateY(${translateY}px)`;
      item.style.opacity = progress;
      item.style.transition = `transform 0.6s cubic-bezier(0.77,0,0.175,1), opacity 0.6s`;
    });
  }



  showGroup(index, { cascade = false } = {}) {
    index = Math.max(0, Math.min(index, this.totalGroups - 1));
    this.currentGroupIndex = index;

    const activeGroup = this.groups[index];

    this.sliderItems.forEach((item) => {
      item.classList.remove('active');
      item.style.transition = '';
      item.style.opacity = '';
      item.style.transform = 'translateY(50px)'; // Set to starting offset
    });

    activeGroup.forEach((item, i) => {
      item.style.transition = `opacity 0.6s ease ${i * 100}ms, transform 0.6s ease ${i * 100}ms`;
      requestAnimationFrame(() => {
        item.classList.add('active');
      });
    });

    this.dots?.forEach((dot, i) => {
      dot.classList.toggle('dot--active', i === index);
      dot.toggleAttribute('aria-current', i === index);
    });

    if (this.prevButton) this.prevButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.totalGroups - 1;
  }
}

if (!customElements.get('featured-slider')) {
  customElements.define('featured-slider', FeaturedSlider);
}
class ProductAccordion extends HTMLElement {
  constructor() {
    super();
    this.detail = this.querySelector('.details');
    this.content = this.querySelector('.accordion__content');
    if (!this.detail.classList.contains('open')) this.content.style.height = '0';
    this.detail.addEventListener('click', (e) => {
      if (this.detail.classList.contains('open')) {
        this.content.style.height = this.content.scrollHeight + 'px'
        setTimeout(() => (this.content.style.height = '0'), 0)
        this.detail.classList.remove('open')
      } else {
        const height = this.content.scrollHeight
        this.content.style.height = height + 'px'
        setTimeout(() => (this.content.style.height = 'auto'), 500)
        this.detail.classList.add('open')
      }
    })
  }
}

if (!customElements.get('product-accordion')) {
  customElements.define('product-accordion', ProductAccordion);
}
